import React, { useState, useEffect, useRef } from 'react';
import { CustomTheme } from '../types';

interface LabelEditorProps {
  originalLabel: string;
  currentColor: string;
  onSave: (newLabel: string, newColor: string) => void;
  onCancel: () => void;
  isOpen: boolean;
  position?: { top: number; left: number };
  savedThemes?: CustomTheme[];
  onSaveTheme?: (theme: Omit<CustomTheme, 'id'>) => void;
  onDelete?: (label: string, alsoDeleteLabelDefinition: boolean) => void;
  onMerge?: (fromLabel: string, toLabel: string) => void;
  allLabels?: string[];
}

// Predefined color options from our palette
const COLOR_OPTIONS = [
  { name: 'Red (Disease)', class: 'entity-disease', bg: 'bg-red-100', text: 'text-red-800', sample: '#fee2e2/#991b1b' },
  { name: 'Green (Symptom)', class: 'entity-symptom', bg: 'bg-green-100', text: 'text-green-800', sample: '#dcfce7/#166534' },
  { name: 'Blue (Drug)', class: 'entity-drug', bg: 'bg-blue-100', text: 'text-blue-800', sample: '#dbeafe/#1e40af' },
  { name: 'Purple (Treatment)', class: 'entity-treatment', bg: 'bg-purple-100', text: 'text-purple-800', sample: '#f3e8ff/#6b21a8' },
  { name: 'Yellow (Anatomy)', class: 'entity-anatomy', bg: 'bg-yellow-100', text: 'text-yellow-800', sample: '#fef9c3/#854d0e' },
  { name: 'Cyan (Dosage)', class: 'entity-dosage', bg: 'bg-cyan-100', text: 'text-cyan-800', sample: '#cffafe/#155e75' },
  { name: 'Lime (Frequency)', class: 'entity-frequency', bg: 'bg-lime-100', text: 'text-lime-800', sample: '#ecfccb/#3f6212' },
  { name: 'Orange (Duration)', class: 'entity-duration', bg: 'bg-orange-100', text: 'text-orange-800', sample: '#ffedd5/#9a3412' },
  { name: 'Gray (Default)', class: 'entity-default', bg: 'bg-gray-100', text: 'text-gray-800', sample: '#f3f4f6/#1f2937' },
];

const LabelEditor: React.FC<LabelEditorProps> = ({
  originalLabel,
  currentColor,
  onSave,
  onCancel,
  isOpen,
  position,
  savedThemes = [],
  onSaveTheme,
  onDelete,
  onMerge,
  allLabels = []
}) => {
  const [labelValue, setLabelValue] = useState(originalLabel);
  const [selectedColor, setSelectedColor] = useState(currentColor);
  const [customColorMode, setCustomColorMode] = useState(
    !COLOR_OPTIONS.some(opt => opt.class === currentColor)
  );
  const [customBgColor, setCustomBgColor] = useState('#dbeafe');
  const [customTextColor, setCustomTextColor] = useState('#1e40af');
  const [customBorderColor, setCustomBorderColor] = useState('#1e40af');
  const [mergeTarget, setMergeTarget] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [alsoDeleteLabelDef, setAlsoDeleteLabelDef] = useState(true);
  const popupRef = useRef<HTMLDivElement>(null);
  const labelInputRef = useRef<HTMLInputElement>(null);

  // Parse custom color format: "bg:#hex|text:#hex|border:#hex" or older formats
  const parseCustomColor = (color: string) => {
    if (color.includes('|')) {
      const parts = color.split('|');
      const bg = parts[0].replace('bg:', '');
      const text = parts[1].replace('text:', '');
      const border = parts[2] ? parts[2].replace('border:', '') : text; // Default border to text color if not specified
      return { bg, text, border };
    }
    // Legacy single color format - use it as text color with light background
    return { bg: color + '20', text: color, border: color };
  };

  // Reset state when popup opens
  useEffect(() => {
    if (isOpen) {
      setLabelValue(originalLabel);
      setSelectedColor(currentColor);
      const isCustom = !COLOR_OPTIONS.some(opt => opt.class === currentColor);
      setCustomColorMode(isCustom);
      
      if (isCustom && (currentColor.startsWith('#') || currentColor.startsWith('rgb') || currentColor.includes('|'))) {
        const parsed = parseCustomColor(currentColor);
        setCustomBgColor(parsed.bg.replace('20', '') || '#dbeafe');
        setCustomTextColor(parsed.text || '#1e40af');
        setCustomBorderColor(parsed.border || '#1e40af');
      } else {
        setCustomBgColor('#dbeafe');
        setCustomTextColor('#1e40af');
        setCustomBorderColor('#1e40af');
      }
    }
  }, [isOpen, originalLabel, currentColor]);

  // Handle Escape key and click outside
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel();
      }
    };
    
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onCancel();
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      window.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel]);

  const handleSave = () => {
    const finalColor = customColorMode 
      ? `bg:${customBgColor}|text:${customTextColor}|border:${customBorderColor}` 
      : selectedColor;
    if (labelValue.trim()) {
      onSave(labelValue.trim(), finalColor);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't interfere with select dropdowns or buttons
    const target = e.target as HTMLElement;
    if (target.tagName === 'SELECT' || target.tagName === 'BUTTON') {
      return;
    }
    
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  if (!isOpen) return null;

  // Calculate popup position - use absolute positioning so it scrolls with the page
  const popupStyle: React.CSSProperties = {
    position: 'absolute',
    top: position?.top || 100,
    left: position?.left || 100,
    zIndex: 9999,
  };

  return (
    <div 
      ref={popupRef}
      className="bg-white rounded-lg shadow-2xl border-2 border-blue-500 max-w-md w-96"
      style={popupStyle}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* Popup Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">✏️ Edit Label</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-white rounded"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Popup Body */}
      <div className="px-4 py-3">
        <div className="flex flex-col space-y-3">
      {/* Label Name Input */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Label Name
        </label>
        <input
          ref={labelInputRef}
          type="text"
          value={labelValue}
          onChange={(e) => setLabelValue(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          autoFocus
          placeholder="Enter label name..."
        />
      </div>

      {/* Color Selection */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Label Color
        </label>
        
        {/* Saved Custom Themes */}
        {savedThemes.length > 0 && (
          <div className="mb-3">
            <div className="text-xs text-gray-500 mb-1 font-medium">Saved Themes</div>
            <div className="grid grid-cols-3 gap-2">
              {savedThemes.map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => {
                    setSelectedColor(`bg:${theme.bgColor}|text:${theme.textColor}|border:${theme.borderColor}`);
                    setCustomColorMode(false);
                    // Refocus input so Enter key saves instead of re-clicking this button
                    setTimeout(() => labelInputRef.current?.focus(), 0);
                  }}
                  className={`p-2 rounded border-2 transition-all ${
                    !customColorMode && selectedColor === `bg:${theme.bgColor}|text:${theme.textColor}|border:${theme.borderColor}`
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  title={theme.name}
                >
                  <div 
                    className="px-2 py-1 rounded text-xs font-medium text-center border-2"
                    style={{
                      backgroundColor: theme.bgColor,
                      color: theme.textColor,
                      borderColor: theme.borderColor
                    }}
                  >
                    {theme.name}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Predefined Colors Grid */}
        <div className="text-xs text-gray-500 mb-1 font-medium">Standard Colors</div>
        <div className="grid grid-cols-3 gap-2 mb-2">
          {COLOR_OPTIONS.map((option) => (
            <button
              key={option.class}
              onClick={() => {
                setSelectedColor(option.class);
                setCustomColorMode(false);
                // Refocus input so Enter key saves instead of re-clicking this button
                setTimeout(() => labelInputRef.current?.focus(), 0);
              }}
              className={`p-2 rounded border-2 transition-all ${
                !customColorMode && selectedColor === option.class
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              title={option.name}
            >
              <div className={`${option.bg} ${option.text} px-2 py-1 rounded text-xs font-medium text-center`}>
                Sample
              </div>
            </button>
          ))}
        </div>

        {/* Custom Color Option */}
        <div className="border-t border-gray-200 pt-2">
          <div
            className={`w-full p-3 rounded border-2 transition-all ${
              customColorMode
                ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div 
              className="flex items-center justify-between mb-2 cursor-pointer"
              onClick={() => setCustomColorMode(!customColorMode)}
            >
              <span className="text-xs font-semibold text-gray-700">🎨 Custom Color Theme</span>
            </div>
            
            {customColorMode && (
              <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                {/* Background Color Picker */}
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-600 w-20">Background:</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={customBgColor}
                      onChange={(e) => setCustomBgColor(e.target.value)}
                      className="w-10 h-8 rounded border border-gray-300 cursor-pointer"
                      title="Pick background color"
                    />
                    <input
                      type="text"
                      value={customBgColor}
                      onChange={(e) => setCustomBgColor(e.target.value)}
                      className="w-20 px-2 py-1 text-xs border border-gray-300 rounded font-mono"
                      placeholder="#hex"
                    />
                  </div>
                </div>
                
                {/* Text Color Picker */}
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-600 w-20">Text:</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={customTextColor}
                      onChange={(e) => setCustomTextColor(e.target.value)}
                      className="w-10 h-8 rounded border border-gray-300 cursor-pointer"
                      title="Pick text color"
                    />
                    <input
                      type="text"
                      value={customTextColor}
                      onChange={(e) => setCustomTextColor(e.target.value)}
                      className="w-20 px-2 py-1 text-xs border border-gray-300 rounded font-mono"
                      placeholder="#hex"
                    />
                  </div>
                </div>
                
                {/* Border Color Picker */}
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-600 w-20">Border:</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={customBorderColor}
                      onChange={(e) => setCustomBorderColor(e.target.value)}
                      className="w-10 h-8 rounded border border-gray-300 cursor-pointer"
                      title="Pick border color"
                    />
                    <input
                      type="text"
                      value={customBorderColor}
                      onChange={(e) => setCustomBorderColor(e.target.value)}
                      className="w-20 px-2 py-1 text-xs border border-gray-300 rounded font-mono"
                      placeholder="#hex"
                    />
                  </div>
                </div>
                
                {/* Live Preview */}
                <div
                  className="mt-2 px-3 py-1.5 rounded text-xs font-medium text-center border-2"
                  style={{
                    backgroundColor: customBgColor,
                    color: customTextColor,
                    borderColor: customBorderColor
                  }}
                >
                  Live Preview
                </div>
                
                {/* Save Theme Button */}
                {onSaveTheme && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSaveTheme({
                        name: 'Custom',
                        bgColor: customBgColor,
                        textColor: customTextColor,
                        borderColor: customBorderColor
                      });
                    }}
                    className="mt-2 w-full py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 border border-blue-300 hover:border-blue-400 rounded transition-colors"
                  >
                    💾 Save as Theme
                  </button>
                )}
              </div>
            )}
            
            {!customColorMode && (
              <div className="text-xs text-gray-500 text-center">
                Click to create custom theme
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="border-t border-gray-200 pt-3">
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Final Preview
        </label>
        <div className="flex items-center justify-center p-4 bg-gray-50 rounded">
          {customColorMode ? (
            <span
              className="inline-block px-3 py-1 rounded text-xs font-medium border-2"
              style={{
                backgroundColor: customBgColor,
                color: customTextColor,
                borderColor: customBorderColor
              }}
            >
              {labelValue || 'Label Text'}
            </span>
          ) : (
            <span className={`entity-badge ${selectedColor}`}>
              {labelValue || 'Label Text'}
            </span>
          )}
        </div>
      </div>

      {/* Batch Operations */}
      {(onDelete || onMerge) && (
        <div className="border-t border-gray-200 pt-3 px-4">
          <div className="text-xs font-semibold text-gray-700 mb-2">Batch Operations</div>
          
          {/* Merge Label */}
          {onMerge && allLabels.length > 1 && (
            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1">
                Merge "{originalLabel}" into:
              </label>
              <div className="flex gap-2">
                <select
                  value={mergeTarget}
                  onChange={(e) => setMergeTarget(e.target.value)}
                  className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select target label...</option>
                  {allLabels
                    .filter(l => l !== originalLabel)
                    .map(label => (
                      <option key={label} value={label}>{label}</option>
                    ))
                  }
                </select>
                <button
                  onClick={() => {
                    if (mergeTarget && onMerge) {
                      if (window.confirm(`Merge all "${originalLabel}" annotations into "${mergeTarget}"? This cannot be undone.`)) {
                        onMerge(originalLabel, mergeTarget);
                      }
                    }
                  }}
                  disabled={!mergeTarget}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Merge
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                All annotations with this label will be changed to the target label
              </p>
            </div>
          )}
          
          {/* Delete Label */}
          {onDelete && (
            <div className="mb-2">
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                >
                  🗑️ Delete Label from All Examples
                </button>
              ) : (
                <div className="bg-red-50 border border-red-300 rounded p-2">
                  <p className="text-xs text-red-800 font-medium mb-2">
                    Are you sure? This will delete all "{originalLabel}" annotations from every example.
                  </p>
                  <label className="flex items-center gap-2 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={alsoDeleteLabelDef}
                      onChange={(e) => setAlsoDeleteLabelDef(e.target.checked)}
                      className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    />
                    <span className="text-xs text-gray-700">
                      Also remove this label from the available labels list
                    </span>
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setAlsoDeleteLabelDef(true);
                      }}
                      className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (onDelete) {
                          onDelete(originalLabel, alsoDeleteLabelDef);
                          setShowDeleteConfirm(false);
                          setAlsoDeleteLabelDef(true);
                        }
                      }}
                      className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
                    >
                      Delete All
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Keyboard Hints */}
      <div className="text-xs text-gray-500 text-center pt-1 pb-2 px-4">
        <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">Enter</kbd> to save • <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">Esc</kbd> to cancel
      </div>
        </div>
      </div>
      
      {/* Popup Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end space-x-2 rounded-b-lg">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!labelValue.trim()}
          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default LabelEditor;

